import { useContext } from "react";
import { DeclarationContext } from "../components/context/DeclarationWrapper";

/**
 * Hook pour gérer la validation des déclarations à montant zéro (attestations)
 *
 * Gère 4 cas de validation :
 * - Cas 1: Attestation valide (montant = 0 ET effectifs = 0)
 * - Cas 2: Déclaration normale valide (montant > 0 ET effectifs > 0)
 * - Cas 3: Erreur (montant = 0 MAIS effectifs > 0)
 * - Cas 4: Erreur (montant > 0 MAIS effectifs = 0)
 *
 * @returns {Object} Fonctions de validation
 */
export const useZeroAmountValidation = () => {
  const { isAttestation, getValidationError } = useContext(DeclarationContext);

  /**
   * Vérifie si une déclaration est une attestation valide
   * @param {Object} declaration - Objet déclaration avec feeAmount et workForce
   * @returns {boolean}
   */
  const isValidAttestation = (declaration) => {
    return isAttestation(declaration);
  };

  /**
   * Vérifie si une déclaration est une facture normale valide
   * @param {Object} declaration - Objet déclaration avec feeAmount et workForce
   * @returns {boolean}
   */
  const isValidNormalDeclaration = (declaration) => {
    const amount = parseFloat(declaration?.feeAmount || 0);
    const workForce = parseInt(declaration?.workForce || 0, 10);

    return amount > 0 && workForce > 0;
  };

  /**
   * Vérifie s'il y a une erreur de validation
   * @param {Object} declaration - Objet déclaration avec feeAmount et workForce
   * @returns {string|null} Message d'erreur ou null si valide
   */
  const getValidationErrorMessage = (declaration) => {
    return getValidationError(declaration);
  };

  /**
   * Détermine le type de déclaration
   * @param {Object} declaration - Objet déclaration avec feeAmount et workForce
   * @returns {string} 'attestation', 'normal', ou 'error'
   */
  const getDeclarationType = (declaration) => {
    const validationError = getValidationErrorMessage(declaration);

    if (validationError) {
      return 'error';
    }

    if (isValidAttestation(declaration)) {
      return 'attestation';
    }

    if (isValidNormalDeclaration(declaration)) {
      return 'normal';
    }

    return 'error';
  };

  /**
   * Valide une déclaration et retourne le résultat
   * @param {Object} declaration - Objet déclaration avec feeAmount et workForce
   * @returns {Object} { isValid: boolean, type: string, error: string|null }
   */
  const validateDeclaration = (declaration) => {
    const type = getDeclarationType(declaration);
    const error = getValidationErrorMessage(declaration);

    return {
      isValid: type !== 'error',
      type,
      error,
    };
  };

  /**
   * Détermine si une transition nécessite un fichier DSN
   * @param {Object} currentDeclaration - Déclaration actuelle
   * @param {Object} newDeclaration - Nouvelle déclaration
   * @returns {boolean}
   */
  const requiresDsnFile = (currentDeclaration, newDeclaration) => {
    const currentType = currentDeclaration?.billType;
    const newType = getDeclarationType(newDeclaration);

    // Facture → Attestation : nécessite un fichier DSN
    if (
      currentType !== 'attestation' &&
      currentType !== undefined &&
      newType === 'attestation'
    ) {
      return true;
    }

    // Attestation → Attestation : pas de DSN nécessaire (déjà stocké)
    if (currentType === 'attestation' && newType === 'attestation') {
      return false;
    }

    // Attestation → Facture : pas de DSN nécessaire
    if (currentType === 'attestation' && newType === 'normal') {
      return false;
    }

    // Création initiale d'une attestation : nécessite un DSN
    if (currentType === undefined && newType === 'attestation') {
      return true;
    }

    return false;
  };

  /**
   * Obtient le message de transition
   * @param {Object} currentDeclaration - Déclaration actuelle
   * @param {Object} newDeclaration - Nouvelle déclaration
   * @returns {string}
   */
  const getTransitionMessage = (currentDeclaration, newDeclaration) => {
    const currentType = currentDeclaration?.billType;
    const newType = getDeclarationType(newDeclaration);

    if (currentType === 'attestation' && newType === 'attestation') {
      return 'Modification d\'une attestation existante';
    }

    if (currentType !== 'attestation' && newType === 'attestation') {
      return 'Conversion de facture vers attestation (nécessite un fichier DSN)';
    }

    if (currentType === 'attestation' && newType === 'normal') {
      return 'Conversion d\'attestation vers facture';
    }

    if (newType === 'attestation') {
      return 'Création d\'une nouvelle attestation (nécessite un fichier DSN)';
    }

    return 'Modification standard';
  };

  /**
   * Messages d'erreur standards
   */
  const ERROR_MESSAGES = {
    ZERO_AMOUNT_WITH_WORKFORCE: "Une déclaration à montant nul doit avoir 0 effectif déclaré.",
    POSITIVE_AMOUNT_WITHOUT_WORKFORCE: "Une déclaration avec effectif = 0 doit avoir un montant nul.",
    OPTIONAL_WORKER_REQUIRED: "Vous devez cocher la case confirmant que vous avez pris connaissance que la souscription aux Prestations pour les Ouvriers est optionnelle.",
  };

  /**
   * Valide la checkbox optionnelle pour les ouvriers
   * @param {number} workForce - Nombre d'effectifs
   * @param {boolean} agreeOptionalWorker - Case cochée ou non
   * @param {boolean} conditionCheckbox - La condition est-elle applicable
   * @returns {string|null} Message d'erreur ou null
   */
  const validateOptionalWorkerAgreement = (workForce, agreeOptionalWorker, conditionCheckbox) => {
    if (workForce > 10 && !agreeOptionalWorker && conditionCheckbox) {
      return ERROR_MESSAGES.OPTIONAL_WORKER_REQUIRED;
    }
    return null;
  };

  return {
    // Validation functions
    isValidAttestation,
    isValidNormalDeclaration,
    getValidationErrorMessage,
    validateDeclaration,
    getDeclarationType,

    // Transition functions
    requiresDsnFile,
    getTransitionMessage,

    // Additional validation
    validateOptionalWorkerAgreement,

    // Constants
    ERROR_MESSAGES,
  };
};
